import { useMemo, useState } from "react";
import { InlineHelp } from "@/components/common/InlineHelp";
import { ManufacturingModelsDictateMicButton } from "@/components/manufacturing/ManufacturingModelsDictateMicButton";
import { ghostBtnClass, inputClass } from "@/components/manufacturing/manufacturingSurfaceClasses";
import {
  extractBuilderAssistantObservation,
  saveBuilderAssistantObservation,
  uploadBuilderAssistantPhoto,
} from "@/lib/manufacturing/builderAssistantClient";
import type { BuilderAssistantBundle } from "@/lib/manufacturing/builderAssistantTypes";
import { BuilderAssistantObservationFix } from "./BuilderAssistantObservationFix";
import { BuilderAssistantFailurePhotos } from "./BuilderAssistantFailurePhotos";
import { BuilderAssistantQcFailureDialog } from "./BuilderAssistantQcFailureDialog";

const TYPES = [
  ["QC_DEFECT", "QC failure"],
  ["CONFORMING_EXAMPLE", "Conforming example"],
  ["REFERENCE_STANDARD", "Reference standard"],
  ["SPEC_DISCOVERY", "Spec discovery"],
  ["IMPROVEMENT_NOTE", "Improvement note"],
  ["NEEDS_REVIEW", "Needs review"],
] as const;

export function BuilderAssistantWalk({
  bundle,
  assumedFixerName,
  onChanged,
}: {
  bundle: BuilderAssistantBundle;
  assumedFixerName: string;
  onChanged: () => void;
}) {
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [photoTarget, setPhotoTarget] = useState<string | null>(null);
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [failure, setFailure] = useState<{ id: string; description: string } | null>(null);

  const evidenceCount = useMemo(() => {
    const counts = new Map<string, number>();
    for (const row of bundle.evidence) {
      if (row.caption === "fix") continue;
      counts.set(row.observation_id, (counts.get(row.observation_id) ?? 0) + 1);
    }
    return counts;
  }, [bundle.evidence]);

  const tagsFor = (observationId: string) =>
    bundle.tag_links.filter((link) => link.observation_id === observationId).map((link) => link.tag_key);

  async function onTranscript(transcript: string): Promise<boolean> {
    setBusy(true);
    setError(null);
    const res = await extractBuilderAssistantObservation(bundle.session.id, transcript);
    setBusy(false);
    if (!res.ok) {
      setError(res.error);
      return false;
    }
    const filed = res.data.observation;
    if (filed?.observation_type === "QC_DEFECT" && filed.description.trim()) {
      setFailure({ id: filed.id, description: filed.description });
    }
    onChanged();
    return true;
  }

  async function attach(observationId: string, file: File) {
    setBusy(true);
    setError(null);
    const res = await uploadBuilderAssistantPhoto(observationId, file);
    setBusy(false);
    setPendingFile(null);
    setPhotoTarget(null);
    if (!res.ok) {
      setError(res.error);
      return;
    }
    onChanged();
  }

  function choosePhotoTarget(file: File) {
    const pending = bundle.observations.filter((row) => row.row_status === "pending");
    if (pending.length === 1) {
      void attach(pending[0].id, file);
      return;
    }
    if (bundle.observations.length === 1) {
      void attach(bundle.observations[0].id, file);
      return;
    }
    setPendingFile(file);
    setPhotoTarget(pending[0]?.id ?? bundle.observations[0]?.id ?? null);
  }

  return (
    <section className="space-y-3">
      <ManufacturingModelsDictateMicButton
        extractLabel="Add observation"
        placeholder="Dictate a failure, or say item number one and item number two for several."
        clearOnExtract
        disabled={busy || bundle.session.session_status === "COMPLETED"}
        help={
          <InlineHelp glyph="!" label="How to dictate several observations">
            A failure opens the checklist so you can confirm the section and the item. Say item number one,
            item number two for a clean split. Also, another, and next start a new observation too.
          </InlineHelp>
        }
        onComplete={onTranscript}
      />
      {error ? <p className="text-sm text-red-700 dark:text-red-300">{error}</p> : null}
      <ul className="space-y-3">
        {bundle.observations.map((row) => (
          <li key={row.id} className="space-y-2 rounded-lg border border-gray-200 p-3 dark:border-zinc-700">
            <div className="flex flex-wrap gap-2">
              <select
                className={inputClass}
                value={row.observation_type}
                onChange={(event) => {
                  void saveBuilderAssistantObservation({
                    session_id: bundle.session.id,
                    observation_id: row.id,
                    description: row.description,
                    observation_type: event.target.value,
                    component_id: row.component_id,
                    severity: row.severity,
                    crew_label: row.crew_label,
                    tag_keys: tagsFor(row.id),
                    row_status: "saved",
                  }).then(() => {
                    if (event.target.value === "QC_DEFECT" && row.description.trim()) {
                      setFailure({ id: row.id, description: row.description });
                    }
                    onChanged();
                  });
                }}
              >
                {TYPES.map(([type, label]) => (
                  <option key={type} value={type}>
                    {label}
                  </option>
                ))}
              </select>
              <select
                className={inputClass}
                value={row.component_id ?? ""}
                onChange={(event) => {
                  void saveBuilderAssistantObservation({
                    session_id: bundle.session.id,
                    observation_id: row.id,
                    description: row.description,
                    observation_type: row.observation_type,
                    component_id: event.target.value || null,
                    severity: row.severity,
                    crew_label: row.crew_label,
                    tag_keys: tagsFor(row.id),
                    row_status: "saved",
                  }).then(() => onChanged());
                }}
              >
                <option value="">Component</option>
                {bundle.components.map((component) => (
                  <option key={component.id} value={component.id}>
                    {component.name}
                  </option>
                ))}
              </select>
            </div>
            <textarea
              className={`${inputClass} min-h-16 w-full`}
              defaultValue={row.description}
              onBlur={(event) => {
                if (event.target.value === row.description) return;
                void saveBuilderAssistantObservation({
                  session_id: bundle.session.id,
                  observation_id: row.id,
                  description: event.target.value,
                  observation_type: row.observation_type,
                  component_id: row.component_id,
                  severity: row.severity,
                  crew_label: row.crew_label,
                  tag_keys: tagsFor(row.id),
                  row_status: "saved",
                }).then(() => onChanged());
              }}
            />
            <p className="text-xs text-gray-500 dark:text-zinc-400">
              Photos: {evidenceCount.get(row.id) ?? 0}
              {row.crew_label ? ` · Crew ${row.crew_label}` : ""}
              {tagsFor(row.id).length ? ` · ${tagsFor(row.id).join(", ")}` : ""}
            </p>
            <BuilderAssistantObservationFix
              observation={row}
              photos={bundle.evidence.filter(
                (photo) => photo.observation_id === row.id && photo.caption === "fix",
              )}
              assumedName={assumedFixerName}
              disabled={busy || bundle.session.session_status === "COMPLETED"}
              onChanged={onChanged}
            />
            {row.observation_type === "QC_DEFECT" ? (
              <button
                type="button"
                className={ghostBtnClass}
                disabled={busy || bundle.session.session_status === "COMPLETED"}
                onClick={() => setFailure({ id: row.id, description: row.description })}
              >
                Choose checklist item
              </button>
            ) : null}
            {row.observation_type === "QC_DEFECT" ? (
              <BuilderAssistantFailurePhotos
                observationId={row.id}
                count={bundle.evidence.filter((photo) => photo.observation_id === row.id && photo.caption !== "fix").length}
                disabled={busy || bundle.session.session_status === "COMPLETED"}
                onChanged={onChanged}
              />
            ) : (
            <label className={`${ghostBtnClass} inline-flex cursor-pointer`}>
              Add photo
              <input
                type="file"
                accept="image/jpeg,image/png,image/webp"
                capture="environment"
                className="hidden"
                onChange={(event) => {
                  const file = event.target.files?.[0];
                  event.target.value = "";
                  if (file) void attach(row.id, file);
                }}
              />
            </label>
            )}
          </li>
        ))}
      </ul>
      {bundle.observations.length > 1 ? (
        <label className={`${ghostBtnClass} inline-flex cursor-pointer`}>
          Photo for…
          <input
            type="file"
            accept="image/jpeg,image/png,image/webp"
            capture="environment"
            className="hidden"
            onChange={(event) => {
              const file = event.target.files?.[0];
              event.target.value = "";
              if (file) choosePhotoTarget(file);
            }}
          />
        </label>
      ) : null}
      {pendingFile && photoTarget ? (
        <div className="space-y-2 rounded-md border border-amber-300 p-3 text-sm dark:border-amber-700">
          <p>Which observation is this photograph for?</p>
          <select
            className={inputClass}
            value={photoTarget}
            onChange={(event) => setPhotoTarget(event.target.value)}
          >
            {bundle.observations.map((row) => (
              <option key={row.id} value={row.id}>
                {row.description.slice(0, 80)}
              </option>
            ))}
          </select>
          <button type="button" className={ghostBtnClass} onClick={() => void attach(photoTarget, pendingFile)}>
            Attach photo
          </button>
        </div>
      ) : null}
      {failure ? (
        <BuilderAssistantQcFailureDialog
          sessionId={bundle.session.id}
          observationId={failure.id}
          description={failure.description}
          hasPhoto={bundle.evidence.some((photo) => photo.observation_id === failure.id && photo.caption !== "fix")}
          onClose={() => setFailure(null)}
          onFiled={() => {
            setFailure(null);
            onChanged();
          }}
        />
      ) : null}
    </section>
  );
}
